import { motion } from "framer-motion";
import {
  ShoppingBag,
  IndianRupee,
  Package,
  CreditCard,
  XCircle,
  RotateCcw,
  Sparkles,
  Search,
  TrendingDown,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { SEO_KEYWORDS_EXAMPLE } from "../../lib/landing-data";

const DASHBOARD_METRICS = [
  { icon: ShoppingBag, label: "Today's Orders", value: "47", tone: "bg-purple-50 text-purple-700" },
  { icon: IndianRupee, label: "Revenue", value: "₹38,200", tone: "bg-emerald-50 text-emerald-700" },
  { icon: Package, label: "COD Orders", value: "29", tone: "bg-amber-50 text-amber-700" },
  { icon: CreditCard, label: "Prepaid", value: "18", tone: "bg-violet-50 text-violet-700" },
  { icon: XCircle, label: "Cancelled", value: "3", tone: "bg-red-50 text-red-600" },
  { icon: RotateCcw, label: "Returns / RTO", value: "2", tone: "bg-slate-100 text-slate-700" },
];

const SectionHeading = ({ eyebrow, title, accent, sub }) => (
  <div className="max-w-3xl">
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider">
      {eyebrow}
    </span>
    <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
      {title}{" "}
      {accent && (
        <span className="bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent">{accent}</span>
      )}
    </h2>
    {sub && <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">{sub}</p>}
  </div>
);

export const DashboardSpotlight = () => {
  return (
    <section id="dashboard-spotlight" className="relative py-20 sm:py-28 bg-gradient-to-b from-white to-purple-50/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Spotlight · Sales Dashboard"
          title="One-tap Sync, full daily report —"
          accent="no downloads. No page-hopping."
          sub="Sync your seller panel data in one click — Easy Lister pulls your latest orders and generates a clean daily report instantly. Just hit Sync and see today's full picture."
        />

        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3 rounded-3xl border border-purple-100 bg-white shadow-2xl shadow-purple-900/10 p-6 sm:p-8"
            data-testid="dashboard-spotlight-mockup"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-heading text-lg font-extrabold text-slate-900">Daily Snapshot</div>
                <div className="text-xs text-slate-500">12 Dec, 2025 · Live</div>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-purple-700 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-600 animate-pulse" /> Auto-refresh
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {DASHBOARD_METRICS.map((m, i) => (
                <motion.div
                  key={m.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl border border-slate-100 bg-gradient-to-br from-white to-slate-50/50 p-4"
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${m.tone}`}>
                    <m.icon className="w-4.5 h-4.5" strokeWidth={2.2} />
                  </div>
                  <div className="text-xs text-slate-500 font-medium">{m.label}</div>
                  <div className="font-heading text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">{m.value}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: ShoppingBag, t: "One-tap Sync", d: "Hit Sync on your seller panel — the latest orders pull in instantly." },
              { icon: Package, t: "COD vs Prepaid", d: "Track fulfillment priority and RTO risk at a glance." },
              { icon: IndianRupee, t: "Today's Revenue", d: "Gross earnings for the day, ready the moment you sync." },
              { icon: RotateCcw, t: "Returns / RTO", d: "Separated from confirmed deliveries — take action fast." },
            ].map((row, i) => (
              <motion.div
                key={row.t}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-start gap-3 rounded-2xl border border-purple-100 bg-white p-4"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
                  <row.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading text-sm font-bold text-slate-900">{row.t}</div>
                  <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{row.d}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const AISpotlight = () => {
  return (
    <section id="ai-spotlight" className="relative py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Spotlight · AI Listing"
          title="Your AI listing assistant —"
          accent="ready in one click."
          sub="Easy Lister&rsquo;s AI reads your product details and writes a complete, platform-ready listing — title, bullets, description, and attributes."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="ai-spotlight-card"
            className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 p-7 sm:p-9 shadow-lg shadow-purple-600/5"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center shadow-lg shadow-purple-600/30">
                <Sparkles className="w-5.5 h-5.5" />
              </div>
              <div className="font-heading text-lg font-extrabold text-slate-900">What the AI generates</div>
            </div>
            <ul className="space-y-3 text-sm sm:text-base text-slate-700">
              {[
                "Product Title — optimized for character limits & search ranking",
                "Description — benefit-led, written for Indian buyers, not keyword-stuffed",
                "Key Features — 5–7 scannable bullets buyers look for",
                "Category-specific Attributes — fabric, size, material, pack count",
                "HSN Code suggestions based on product type",
              ].map((row) => (
                <li key={row} className="flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 rounded-full bg-purple-600 shrink-0" />
                  {row}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-3xl border border-slate-200 bg-white p-7 sm:p-9"
          >
            <div className="font-heading text-lg font-extrabold text-slate-900 mb-5">How it works</div>
            <ol className="space-y-4">
              {[
                "Enter your product name and category (or let Easy Lister read it from the page).",
                'Click "Generate with AI".',
                "Review the AI-generated content in a side panel.",
                'Click "Insert into Form" — done.',
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-4">
                  <span className="font-heading text-xl font-extrabold text-purple-600 w-7 shrink-0">0{i + 1}</span>
                  <span className="text-sm sm:text-base text-slate-700 pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-purple-700">
              Built-in safety: avoids banned words & follows platform guidelines.
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const SEOSpotlight = () => {
  return (
    <section id="seo-spotlight" className="relative py-20 sm:py-28 bg-gradient-to-b from-white via-purple-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Spotlight · SEO Keywords"
          title="Rank higher. Sell more."
          accent="Without guessing."
          sub="Easy Lister shows the exact keywords buyers are searching for on Flipkart and Meesho — so your listings show up first."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {[
              "Top 10–20 keywords for your product category",
              "Search volume indicator (High / Medium / Low)",
              "Relevance score per keyword for your product",
              "Platform-specific: separate lists for Flipkart vs Meesho",
              "One-click keyword insertion into title or description",
            ].map((row, i) => (
              <motion.div
                key={row}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="flex items-start gap-3 rounded-2xl border border-purple-100 bg-white p-4"
              >
                <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
                  <Search className="w-4.5 h-4.5" />
                </div>
                <div className="text-sm sm:text-base text-slate-700 pt-1.5 font-medium">{row}</div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="seo-spotlight-mockup"
            className="rounded-3xl border border-purple-100 bg-white shadow-2xl shadow-purple-900/10 p-6 sm:p-7"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="font-heading text-base font-extrabold text-slate-900">Example: Women&rsquo;s Kurti</div>
                <div className="text-xs text-slate-500">Top Meesho keywords · this week</div>
              </div>
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <ul className="space-y-2">
              {SEO_KEYWORDS_EXAMPLE.map((k, i) => (
                <motion.li
                  key={k.kw}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/50 px-3.5 py-2.5 hover:border-purple-200 hover:bg-purple-50/40 transition-colors"
                >
                  <span className="text-sm font-medium text-slate-800">{k.kw}</span>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      k.vol === "High"
                        ? "bg-emerald-100 text-emerald-700"
                        : k.vol === "Medium"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {k.vol}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const ShippingSpotlight = () => {
  return (
    <section id="shipping-spotlight" className="relative py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Spotlight · Shipping Reducer"
          title="Stop losing money on shipping."
          accent="Every. Single. Order."
          sub="Meesho determines shipping weight from your product image metadata. The wrong image = the wrong (higher) shipping slab — and you silently pay the difference."
        />

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-red-100 bg-white p-7 sm:p-9"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center">
                <TrendingDown className="w-5 h-5" />
              </div>
              <div className="font-heading text-lg font-extrabold text-slate-900">Without Shipping Reducer</div>
            </div>
            <ul className="space-y-3 text-sm sm:text-base text-slate-600">
              {[
                "Random image uploaded — gets a high shipping slab",
                "Pay ₹25–₹40 shipping on a ₹200 product",
                "Margin eroded on every order silently",
                "100 orders/month = ₹2,500–₹4,000 lost",
              ].map((r) => (
                <li key={r} className="flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 rounded-full bg-red-400 shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-purple-50 p-7 sm:p-9 shadow-lg shadow-emerald-500/5"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div className="font-heading text-lg font-extrabold text-slate-900">With Easy Lister</div>
            </div>
            <ul className="space-y-3 text-sm sm:text-base text-slate-700">
              {[
                "Tests image variants automatically",
                "Locks in the image with the lowest shipping slab",
                "Save ₹15–₹35 per order",
                "On 100 orders/month: save ₹1,500–₹3,500",
                "Works on new listings AND existing ones",
              ].map((r) => (
                <li key={r} className="flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
            <div className="mt-6 p-4 rounded-2xl bg-white border border-emerald-100 italic text-sm text-slate-600">
              &ldquo;One listing optimized = savings on every order for life.&rdquo;
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
